/* global Skills */

// SKILL TREE Prop types:
//   * id: string, required - need for getting tree outside of the component. Example: Skills.components[<id>]
//                            Skill tree has available methods:
//                            Skills.components[<id>].getTree() - return the collection
//                            Skills.components[<id>].setTree([...]) - apply new collection
//                            Skills.components[<id>].loadingState() - call loader on the tree
//   * tree: collection of skills, required
// NODE Prop types:
//   * skill_id: number, required
//   * name: string, required
//   * children: array, required
//   * hr_stars: number || undefined || null
//   * candidate_stars: number || null
//   * mandatory: boolean || null
//   * main: boolean || null
//   * readOnly: boolean
// OPTIONS
//   * hideEmptyStars - hide null hr stars
//   * hideMandatory - hide mandatory checkbox
//   * hideMain - hide main checkbox
//   * hideCandidateStars - hide candidate stars
//   * readOnly - set readOnly true for hr stars && mandatory checkbox

import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import SkillsHeadline from './SkillsHeadline.js'
import SkillNode from './SkillNode.js'

const SkillsTree = (props) => {
  const propsRef = useRef(props)
  const [internalState, setInternalState] = useState({
    loading: false,
    tree: applyOptions(props.tree || [], props.options)
  })

  useEffect(() => {
    propsRef.current = props
  })

  const instance = useRef(null)

  // SkillsPositionSelect (a separate, independently-mounted react_component) reads
  // Skills.components[id] from a componentDidMount-scheduled setTimeout(..., 1) right
  // after mount, to trigger the initial "All positions" load. A plain useEffect here
  // runs as a deferred passive effect and can lose that race, silently warning "Tree
  // not yet ready" and never firing the initial skills request. useLayoutEffect runs
  // synchronously at the end of this component's own commit, before any macrotask
  // (setTimeout included) gets a chance to run, so the registration is always ready in
  // time regardless of mount order between the two components.
  useLayoutEffect(() => {
    instance.current = {
      props: propsRef.current,
      state: internalState,

      setState: (newState) => {
        setInternalState(prev => {
          const updated = typeof newState === 'function' ? newState(prev) : newState
          instance.current.state = { ...prev, ...updated }
          return { ...prev, ...updated }
        })
      },

      getTree: () => instance.current.state.tree,

      setTree: (tree) => {
        instance.current.state = { tree, loading: false }
        setInternalState({ tree, loading: false })
      },

      loadingState: () => {
        instance.current.state.loading = true
        setInternalState(prev => ({ ...prev, loading: true }))
      }
    }

    if (!Skills.components) Skills.components = {}
    Skills.components[props.id] = instance.current
    Skills.additionalSkillsInit(props.id)

    return () => {
      if (Skills.components?.[props.id]) {
        delete Skills.components[props.id]
      }
    }
  }, [])

  useEffect(() => {
    if (props.tree !== instance.current?.props.tree ||
      props.options !== instance.current?.props.options) {
      const newTree = applyOptions(props.tree || [], props.options)
      instance.current.props = props
      instance.current.state.tree = newTree
      setInternalState(prev => ({ ...prev, tree: newTree }))
    }
  }, [props.tree, props.options])

  const { loading, tree } = internalState
  const { id, options } = props

  return (
    <div className='skills-tree'>
      {!options.hideHeadline && <SkillsHeadline />}
      <ul className={`skills-tree ${loading ? 'skills-tree-loading' : ''}`} id={id}>
        {tree.length
          ? (
              tree.map((skill) => (
              <SkillNode
                key={skill.skill_id}
                hideCandidateStars={options.hideCandidateStars}
                {...skill}
              />
              ))
            )
          : (
            <p className='text-center italic text-muted padding10 hidden-print'>
              {I18n.t('no_results')}
            </p>
            )}
      </ul>
    </div>
  )
}

SkillsTree.propTypes = {
  id: PropTypes.string.isRequired,
  tree: PropTypes.array.isRequired,
  options: PropTypes.object.isRequired
}

function applyOptions (tree, options) {
  const processedTree = JSON.parse(JSON.stringify(tree || []))
  if (options?.hideEmptyStars) removeEmptyStars(processedTree)
  if (options?.hideMandatory) removeMandatory(processedTree)
  if (options?.hideMain) removeMain(processedTree)
  if (options?.readOnly) setReadOnly(processedTree)
  return processedTree
}

function removeEmptyStars (tree) {
  if (!Array.isArray(tree)) return
  tree.forEach((node) => {
    if (!node.hr_stars) node.hr_stars = undefined
    if (node.children && Array.isArray(node.children)) {
      removeEmptyStars(node.children)
    }
  })
}

function removeMandatory (tree) {
  if (!Array.isArray(tree)) return
  tree.forEach((node) => {
    delete node.mandatory
    if (node.children && Array.isArray(node.children)) {
      removeMandatory(node.children)
    }
  })
}

function removeMain (tree) {
  if (!Array.isArray(tree)) return
  tree.forEach((node) => {
    delete node.main
    if (node.children && Array.isArray(node.children)) {
      removeMain(node.children)
    }
  })
}

function setReadOnly (tree) {
  if (!Array.isArray(tree)) return
  tree.forEach((node) => {
    node.readOnly = true
    if (node.children && Array.isArray(node.children)) {
      setReadOnly(node.children)
    }
  })
}

export default SkillsTree
